import React from 'react';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { SmallPortalTooltip } from '../Tooltip';

// --- Types ---
export type RadarDatum = {
  label: string;
  value: number | null; // 0..max
  tooltipChildren?: React.ReactNode;
};

export type RadarDataset = {
  id: string;
  label: string;
  data: RadarDatum[];
  color: string;
  fillOpacity?: number;
  strokeWidth?: number;
  metadata?: {
    participants?: number;
    completionRate?: number;
  };
};

// --- Utility Functions ---
function polarToCartesian(
  cx: number,
  cy: number,
  radius: number,
  angle: number
) {
  const x = cx + radius * Math.cos(angle);
  const y = cy + radius * Math.sin(angle);
  return { x, y };
}

function wrapLabel(
  label: string,
  maxCharsPerLine = 16,
  maxLines = 3
): string[] {
  const words = label.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current.length === 0 ? word : `${current} ${word}`;
    if (candidate.length <= maxCharsPerLine) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current.length > 0) lines.push(current);

  if (lines.length <= maxLines) return lines;
  // If we have too many lines, merge extras into the last line to stay within maxLines
  const compact = lines.slice(0, maxLines - 1);
  compact.push(lines.slice(maxLines - 1).join(' '));
  return compact;
}

// --- Styled Components ---
const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1/1;
  min-height: 280px;
`;

// --- RadarChart Component ---
export interface RadarChartProps {
  datasets: RadarDataset[];
  max?: number;
  levels?: number;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function RadarChart({
  datasets,
  max = 10,
  levels = 5,
  size = 420,
  className,
  style,
}: RadarChartProps) {
  const [hoveredPoint, setHoveredPoint] =
    React.useState<{
      datasetIndex: number;
      pointIndex: number;
      datum: RadarDatum;
      dataset: RadarDataset;
    } | null>(null);

  const theme = useTheme();
  const center = size / 2;
  const outerMargin = 70; // keep labels inside the viewBox
  const radius = center - outerMargin;
  const labelOffset = 30; // push labels a bit outside the outer ring

  // Use the first dataset to determine dimensions/labels
  const referenceData = datasets[0]?.data || [];
  const angleStep = (Math.PI * 2) / referenceData.length;
  const ringRadii = Array.from(
    { length: levels },
    (_, i) => radius * ((i + 1) / levels)
  );

  return (
    <ChartContainer className={className} style={style}>
      <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%">
        {/* Grid rings */}
        {ringRadii.map((r, idx) => (
          <circle
            style={{
              zIndex: 3,
            }}
            key={`ring-${r}`}
            cx={center}
            cy={center}
            r={r}
            fill="none"
            stroke={theme.colors.textRegular}
            strokeWidth={1}
            opacity={0.7 - idx * (0.6 / levels)}
          />
        ))}

        {/* Axes */}
        {referenceData.map((d, i) => {
          const angle = -Math.PI / 2 + i * angleStep;
          const end = polarToCartesian(center, center, radius, angle);
          const labelPos = polarToCartesian(
            center,
            center,
            radius * 1.1 + labelOffset,
            angle
          );
          const lines = [
            ...wrapLabel(d.label, 16, 3),
            ...(d.value ? [`(${d.value})`] : []),
          ];
          const lineHeight = 14;
          const baseY = labelPos.y - ((lines.length - 1) * lineHeight) / 10;

          // Find the corresponding datum in the first dataset to check for tooltip
          const hasCursorPointer =
            datasets[0]?.data[i]?.tooltipChildren != null;

          return (
            <g key={`axis-${i}`}>
              <line
                x1={center}
                y1={center}
                x2={end.x}
                y2={end.y}
                stroke={theme.colors.bgActive}
                strokeWidth={1}
                opacity={1}
              />
              <text
                x={labelPos.x}
                y={baseY}
                textAnchor="middle"
                fontSize={12}
                fill="currentColor"
                style={{
                  cursor: hasCursorPointer ? 'pointer' : 'default',
                }}
                onMouseEnter={() => {
                  if (datasets[0]?.data[i]?.tooltipChildren) {
                    setHoveredPoint({
                      datasetIndex: 0,
                      pointIndex: i,
                      datum: datasets[0].data[i],
                      dataset: datasets[0],
                    });
                  }
                }}
                onMouseLeave={() => setHoveredPoint(null)}
              >
                {lines.map((line, idx) => (
                  <tspan
                    key={idx}
                    x={labelPos.x}
                    dy={idx === 0 ? 0 : lineHeight}
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}

        {/* Gradient definitions */}
        <defs>
          {datasets.map((dataset) => (
            <linearGradient
              key={`gradient-${dataset.id}`}
              id={`areaFill-${dataset.id}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={dataset.color}
                stopOpacity={dataset.fillOpacity || 0.3}
              />
              <stop
                offset="100%"
                stopColor={dataset.color}
                stopOpacity={(dataset.fillOpacity || 0.3) * 0.3}
              />
            </linearGradient>
          ))}
        </defs>

        {/* Render polygons in reverse order - first dataset renders on top */}
        {[...datasets].reverse().map((dataset) => {
          const points = dataset.data
            .filter(
              (
                d
              ): d is {
                value: NonNullable<typeof d.value>;
                label: NonNullable<typeof d.label>;
                tooltipChildren: NonNullable<typeof d.tooltipChildren>;
              } => d.value !== null
            )
            .map((d, i) => {
              const angle = -Math.PI / 2 + i * angleStep;
              const r = (d.value / max) * radius;
              return polarToCartesian(center, center, r, angle);
            });
          const pointString = points.map((p) => `${p.x},${p.y}`).join(' ');

          // Use solid transparent fill if fillOpacity is 0, otherwise use gradient
          const fillValue =
            dataset.fillOpacity === 0 ? 'none' : `url(#areaFill-${dataset.id})`;

          return (
            <polygon
              key={`polygon-${dataset.id}`}
              points={pointString}
              fill={fillValue}
              stroke={dataset.color}
              strokeWidth={dataset.strokeWidth || 2}
              style={{ transition: 'all 300ms ease-in-out' }}
            />
          );
        })}

        {/* Render points in reverse order - first dataset renders on top */}
        {[...datasets].reverse().map((dataset, reverseIndex) => {
          const datasetIndex = datasets.length - 1 - reverseIndex;
          const points = dataset.data
            .filter(
              (
                d
              ): d is {
                value: NonNullable<typeof d.value>;
                label: NonNullable<typeof d.label>;
                tooltipChildren: NonNullable<typeof d.tooltipChildren>;
              } => d.value !== null
            )
            .map((d, i) => {
              const angle = -Math.PI / 2 + i * angleStep;
              const r = (d.value / max) * radius;
              return polarToCartesian(center, center, r, angle);
            });

          return points.map((p, i) => {
            const datum = dataset.data[i];
            const pointId = `radar-point-${datasetIndex}-${i}`;

            return (
              <React.Fragment key={`pt-${datasetIndex}-${i}`}>
                <circle
                  id={pointId}
                  cx={p.x}
                  cy={p.y}
                  r={5}
                  fill={dataset.color}
                  stroke={theme.colors.textActive}
                  strokeWidth={1}
                  style={{
                    cursor: datum.tooltipChildren ? 'pointer' : 'default',
                    zIndex: 3,
                  }}
                  onMouseEnter={() => {
                    if (datum.tooltipChildren) {
                      setHoveredPoint({
                        datasetIndex,
                        pointIndex: i,
                        datum,
                        dataset,
                      });
                    }
                  }}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
                {hoveredPoint?.datasetIndex === datasetIndex &&
                  hoveredPoint?.pointIndex === i &&
                  datum.tooltipChildren && (
                    <SmallPortalTooltip
                      anchorSelect={`#${pointId}`}
                      place="top"
                      openOnClick={false}
                      isOpen={true}
                    >
                      {datum.tooltipChildren}
                    </SmallPortalTooltip>
                  )}
              </React.Fragment>
            );
          });
        })}
      </svg>
    </ChartContainer>
  );
}

export default RadarChart;
